import React, {
  createContext,
  useCallback,
  use,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export type PromptDialogOptions = Readonly<{
  title: string;
  message: string;
  label?: string;
  defaultValue?: string;
}>;

type PromptFn = (options: PromptDialogOptions) => Promise<string | null>;

const PromptDialogContext = createContext<PromptFn | null>(null);

interface PendingRequest {
  options: PromptDialogOptions;
  resolve: (value: string | null) => void;
}

export const PromptDialogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const queueRef = useRef<PendingRequest[]>([]);
  const [current, setCurrent] = useState<PendingRequest | null>(null);
  const [inputValue, setInputValue] = useState("");

  const closeCurrent = useCallback(() => {
    setCurrent(null);
    setInputValue("");
    if (queueRef.current.length > 0) {
      const next = queueRef.current.shift();
      if (next) {
        setCurrent(next);
        setInputValue(next.options.defaultValue ?? "");
      }
    }
  }, []);

  const prompt = useCallback<PromptFn>((options) => {
    return new Promise<string | null>((resolve) => {
      const req: PendingRequest = { options, resolve };
      setCurrent((cur) => {
        if (cur === null) {
          setInputValue(options.defaultValue ?? "");
          return req;
        }
        queueRef.current.push(req);
        return cur;
      });
    });
  }, []);

  const onCancel = useCallback(() => {
    if (current) {
      current.resolve(null);
      closeCurrent();
    }
  }, [current, closeCurrent]);

  const onSubmit = useCallback(() => {
    if (current) {
      current.resolve(inputValue);
      closeCurrent();
    }
  }, [current, inputValue, closeCurrent]);

  const ctxValue = useMemo(() => prompt, [prompt]);

  const opts: PromptDialogOptions | undefined = current?.options;
  const open = Boolean(current);
  const { t } = useTranslation();

  return (
    <PromptDialogContext value={ctxValue}>
      {children}
      <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
        {opts ? (
          <DialogTitle>
            <Typography variant="h6">{opts.title}</Typography>
          </DialogTitle>
        ) : null}
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{opts ? opts.message : ""}</Typography>
          <TextField
            autoFocus
            fullWidth
            label={opts?.label}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>{t("cancel")}</Button>
          <Button onClick={onSubmit} variant="contained" color="primary">
            {t("submit")}
          </Button>
        </DialogActions>
      </Dialog>
    </PromptDialogContext>
  );
};

export const usePromptDialog = (): PromptFn => {
  const ctx = use(PromptDialogContext);
  if (!ctx) {
    throw new Error(
      "usePromptDialog must be used within a PromptDialogProvider"
    );
  }
  return ctx;
};
